/**
 * functions common visitor
 *
 * @category   Javascript
 * @author     Jakob Ohlsen <jakob.ohlsen@gmail.com>
 * @copyright  2025 Jakob Ohlsen
 */
import { getThumbmark } from '@thumbmarkjs/thumbmarkjs';
import { murmurHash3Ascii } from '@ts/common/hash';
// `murmurHash3Ascii` reads input bytes via `charCodeAt & 0xff` and is
// therefore exact only for ASCII. That's acceptable here: thumbmarkjs
// emits JSON-stringified fingerprint data dominated by hex hashes, numbers,
// platform enums, BCP-47 language tags, and IANA timezones — all ASCII.
// Detected font names on non-Latin systems (Japanese / Chinese / Korean)
// can include non-ASCII codepoints which contribute reduced entropy, but
// at 32-bit output the practical collision rate among real users stays at
// the 1/2³² floor. If this caller's input ever shifts toward arbitrary
// unicode (e.g. user-supplied strings), switch to a UTF-8-correct hash.
export async function getPrints() {
    try {
        const { components: fingerprintData } = await getThumbmark();
        const primary_print = await murmurHash3Ascii(JSON.stringify(fingerprintData));
        const secondary_print = (typeof fingerprintData.webgl === 'object' && 'commonPixelsHash' in fingerprintData.webgl && typeof fingerprintData.webgl.commonPixelsHash === 'string') ? fingerprintData.webgl.commonPixelsHash : 'none';
        const tertiary_print = await murmurHash3Ascii(JSON.stringify(fingerprintData.system));
        const platform = (typeof fingerprintData.system === 'object' && 'platform' in fingerprintData.system) ? fingerprintData.system.platform : '';
        const colorDepth = (typeof fingerprintData.screen === 'object' && 'colorDepth' in fingerprintData.screen) ? fingerprintData.screen.colorDepth : '';
        const quaternary_print = await murmurHash3Ascii(JSON.stringify(fingerprintData.plugins)
            + JSON.stringify(platform)
            + JSON.stringify(colorDepth));
        return [
            primary_print,
            secondary_print,
            tertiary_print,
            quaternary_print
        ];
    }
    catch (error) {
        throw error;
    }
}
// export async function performance() {
//     const result = await getThumbmark({ performance: true });
//     return { elapsed: result.elapsed ?? {} };
// }
